import styled from '@emotion/native';
import { DocumentTypeCode, GroupSecurity } from '@quromedical/fhir-common';
import { DocumentGetAllResponse } from '@quromedical/models';
import { Row } from 'components/base';
import { CrudForm } from 'components/form';
import { WithGroup } from 'core/auth';
import {
  createDocumentColumns,
  documentFields,
  DocumentForm,
  DocumentTable,
  mapMedicationsScript,
  medicationNotesFields,
} from 'core/document';
import { PaginatedTable } from 'core/table';
import { Button, Card, MARGIN, Section, Skeleton, SkeletonProvider, Visible } from 'design-system';
import { useDocuments } from 'hooks/useDocument';
import { useList } from 'hooks/useList';
import { FetcherOptions } from 'hooks/usePaginatedData';
import { DocumentApi } from 'integration/aggregate';
import React, { useCallback, useState } from 'react';
import { strings } from 'strings';

import { CreateDocumentFormCard } from './cards';

interface DocumentTabProps {
  patientId: string;
}

const api = new DocumentApi();

const ButtonWrapper = styled(Row)({
  width: '100%',
  marginTop: MARGIN['2xs'],
  justifyContent: 'flex-end',
});

export const DocumentTab: React.FC<DocumentTabProps> = ({ patientId }) => {
  const { nextToken, setPageToken, setCount, documents, isLoading, cacheKey, refetch } =
    useDocuments({
      patient: patientId,
    });

  const [documentFormState, setDocumentFormState] = useState<Partial<DocumentForm>>();
  const [script, setScript] = useState<string>();

  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const isMultiForm = documentFormState?.type === DocumentTypeCode.createScript;

  const fetcher = useCallback(
    async (options?: FetcherOptions) => {
      setPageToken(options?.nextToken);
      setCount(options?.count);
      return { documents, nextToken };
    },
    [setPageToken, setCount, documents, nextToken]
  );

  const columns = createDocumentColumns();
  const handleSubmit = useCallback(
    async (input: DocumentForm | DocumentForm[]) => {
      try {
        const keysToRemove = ['patient', 'type'];
        setSubmitting(true);
        const inputData = Object.fromEntries(
          Object.entries(input).filter(([key]) => !keysToRemove.includes(key))
        );

        await api.createDocument({
          patient: patientId,
          type: documentFormState?.type as DocumentTypeCode,
          data: {
            ...inputData,
          },
        });
        setSubmitting(false);
        setDocumentFormState(undefined);
        await refetch();
        return {};
      } catch (err) {
        return { error: err };
      }
    },
    [documentFormState?.type, patientId, refetch]
  );

  type MedicationFormState = {
    data: Partial<DocumentForm>;
    hasErrors: boolean;
    hasSubmitted: boolean;
  };

  const getDataFromResponse = (getAllResponse: DocumentGetAllResponse): DocumentTable[] =>
    getAllResponse.documents.map(d => {
      return {
        title: d.document.title,
        author: d.author.display || strings.DisplayNotFound,
        date: d.created,
        status: d.status,
        id: d.id,
        type: d.type,
      };
    });

  const list = useList<number, MedicationFormState>([
    {
      key: Math.random(),
      value: {
        data: {},
        hasErrors: true,
        hasSubmitted: false,
      },
    },
  ]);

  const onSelectDocumentType = useCallback(
    async (options?: Partial<DocumentForm>) => {
      setDocumentFormState(options);
      list.reset();
    },
    [list]
  );

  const [hasSubmitted, setHasSubmitted] = useState(false);

  return (
    <SkeletonProvider loading={isLoading}>
      <WithGroup groups={[GroupSecurity.DocumentReferenceCreate]}>
        <Section isVisible={true} unsetZIndex>
          <CrudForm
            title={strings.DocumentCreate}
            includeIcons={true}
            fields={documentFields}
            showSubmitButton={false}
            initialValues={{}}
            onChangeDebounceTime={0}
            onChange={onSelectDocumentType}
          />
          <Card unsetZIndex>
            {list.items.map((p, index) => {
              const isSubmittedItem = p.value.hasSubmitted || hasSubmitted;
              return (
                <CreateDocumentFormCard
                  handleSubmit={handleSubmit}
                  documentType={documentFormState?.type}
                  key={p.key}
                  onAdd={() => {
                    list.update(p.key, { ...p.value, hasErrors: false, hasSubmitted: true });
                    list.add(Math.random(), {
                      data: {},
                      hasErrors: true,
                      hasSubmitted: false,
                    });
                  }}
                  onChange={(data, hasErrors = false) => {
                    list.update(p.key, {
                      hasSubmitted: p.value.hasSubmitted,
                      data,
                      hasErrors,
                    });
                  }}
                  onRemove={() => {
                    list.remove(p.key);
                  }}
                  canAdd={index === list.items.length - 1}
                  canRemove={list.items.length > 1}
                  showErrors={isSubmittedItem && p.value.hasErrors}
                  loading={isSubmitting}
                />
              );
            })}
            <Visible if={isMultiForm}>
              <SkeletonProvider loading={isSubmitting}>
                <Skeleton>
                  <CrudForm
                    cardProps={{ paddingVertical: 0 }}
                    fields={medicationNotesFields}
                    showSubmitButton={false}
                    initialValues={{}}
                    onChangeDebounceTime={0}
                    onChange={data => setScript(data.notes)}
                  />
                </Skeleton>
              </SkeletonProvider>
            </Visible>
            <Visible if={isMultiForm}>
              <ButtonWrapper>
                <Button
                  variant="contained"
                  onPress={async () => {
                    setHasSubmitted(true);
                    const allValid = list.items.every(item => !item.value.hasErrors);
                    if (!allValid) {
                      return;
                    }
                    const formItems = list.items.map(p => p.value.data as DocumentForm);

                    const createScript = mapMedicationsScript(formItems, script);

                    await handleSubmit(createScript as unknown as DocumentForm);
                  }}
                  text={'Submit'}
                />
              </ButtonWrapper>
            </Visible>
          </Card>
        </Section>
      </WithGroup>

      <WithGroup groups={[GroupSecurity.DocumentReferenceRead]}>
        <Section unsetZIndex>
          <Skeleton>
            <PaginatedTable
              cacheKey={cacheKey}
              fetcher={fetcher}
              columns={columns}
              showHeader={true}
              getDataFromResponse={getDataFromResponse}
            />
          </Skeleton>
        </Section>
      </WithGroup>
    </SkeletonProvider>
  );
};
